import {
  FORMA_PAGAMENTO_ROTULO,
  FORMAS_PAGAMENTO,
  type FiltrosRelatorio as FiltrosRelatorioTipo,
  type FormaPagamento,
} from "@caixaflow/shared";
import type { OpcoesFiltro } from "../api/tipos";
import { Campo } from "./Campos";

/**
 * Filtros do Fechamento e da tela de Vendas (mesmos valores na URL, na tela, no CSV e no PDF).
 * `comNumero` mostra a busca pelo nº da venda; `statusPadrao` é a situação que não conta como filtro.
 */
export function PainelFiltros(props: {
  comNumero?: boolean;
  statusPadrao?: FiltrosRelatorioTipo["status"];
  filtros: FiltrosRelatorioTipo;
  opcoes: OpcoesFiltro | undefined;
  souAdmin: boolean;
  definir: (mudanca: Partial<FiltrosRelatorioTipo>) => void;
  limpar: () => void;
}) {
  const { filtros: f, opcoes, souAdmin, definir, limpar, comNumero, statusPadrao = "VALIDA" } = props;
  const um = (lista: string[] | undefined) => lista?.[0] ?? "";
  const lista = (valor: string) => (valor ? [valor] : undefined);
  const produtos = opcoes?.produtos.filter((p) => !f.categoria?.length || f.categoria.includes(p.categoriaId)) ?? [];
  const alternarPagamento = (forma: FormaPagamento) => {
    const atual = new Set(f.pagamento ?? []);
    if (atual.has(forma)) atual.delete(forma);
    else atual.add(forma);
    definir({ pagamento: atual.size ? [...atual] : undefined });
  };
  const algumFiltro = Object.entries(f).some(([k, v]) => (k === "status" ? v !== statusPadrao : Array.isArray(v) ? v.length > 0 : !!v));

  return (
    <section className="card pad form" aria-label="Filtros">
      <div className="linha">
        <h2 style={{ margin: 0 }}>Filtros</h2>
        {algumFiltro && (
          <button className="link-botao" onClick={limpar}>
            Limpar filtros
          </button>
        )}
      </div>
      <div className="grade-filtros">
        {comNumero && (
          <Campo rotulo="Nº da venda">
            <input
              inputMode="numeric"
              placeholder="Ex.: 128"
              value={f.numero ?? ""}
              onChange={(e) => {
                const n = Number(e.target.value.replace(/[^0-9]/g, ""));
                definir({ numero: n > 0 ? n : undefined });
              }}
            />
          </Campo>
        )}
        <Campo rotulo="Evento">
          <select value={um(f.evento)} onChange={(e) => definir({ evento: lista(e.target.value), caixa: undefined })}>
            <option value="">Todos</option>
            {opcoes?.eventos.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.nome}
              </option>
            ))}
          </select>
        </Campo>
        {souAdmin && (
          <Campo rotulo="Operador">
            <select value={um(f.operador)} onChange={(e) => definir({ operador: lista(e.target.value) })}>
              <option value="">Todos</option>
              {opcoes?.operadores.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          </Campo>
        )}
        <Campo rotulo="Caixa">
          <select value={um(f.caixa)} onChange={(e) => definir({ caixa: lista(e.target.value) })}>
            <option value="">Todos</option>
            {opcoes?.caixas.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.numero} · {c.operador}
                {f.evento?.length ? "" : ` · ${c.evento}`}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Categoria">
          <select value={um(f.categoria)} onChange={(e) => definir({ categoria: lista(e.target.value), produto: undefined })}>
            <option value="">Todas</option>
            {opcoes?.categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Produto">
          <select value={um(f.produto)} onChange={(e) => definir({ produto: lista(e.target.value) })}>
            <option value="">Todos</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
                {p.arquivado ? " (arquivado)" : ""}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Situação">
          <select value={f.status} onChange={(e) => definir({ status: e.target.value as FiltrosRelatorioTipo["status"] })}>
            <option value="VALIDA">Válidas</option>
            <option value="CANCELADA">Canceladas</option>
            <option value="TODAS">Válidas e canceladas</option>
          </select>
        </Campo>
        <Campo rotulo="De">
          <input type="datetime-local" value={f.inicio ?? ""} onChange={(e) => definir({ inicio: e.target.value || undefined })} />
        </Campo>
        <Campo rotulo="Até">
          <input type="datetime-local" value={f.fim ?? ""} min={f.inicio} onChange={(e) => definir({ fim: e.target.value || undefined })} />
        </Campo>
      </div>
      <div className="linha-esq" role="group" aria-label="Formas de pagamento">
        <span className="mudo pequeno">Pagamento:</span>
        {FORMAS_PAGAMENTO.map((forma) => (
          <button
            key={forma}
            type="button"
            className={`filtro-chip${f.pagamento?.includes(forma) ? " ativo" : ""}`}
            aria-pressed={!!f.pagamento?.includes(forma)}
            onClick={() => alternarPagamento(forma)}
          >
            {FORMA_PAGAMENTO_ROTULO[forma]}
          </button>
        ))}
      </div>
    </section>
  );
}
